import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSparks } from '../../contexts/SparksContext';
import { formatSparks } from '../../api/sparksApi';
import { bracketFor, levelBadgeUrl, levelProgress, streakBadgeUrl } from './achievementRules';
import './achievements.css';

const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** The hover / tap text of a first-time or count achievement: what to do (with progress and Sparks), or what was done. */
export function achievementHint(a) {
  if (a.earnedAt) return [a.hint?.done, `Earned ${fmtDate(a.earnedAt)}`].filter(Boolean).join(' ');
  const done = a.progress?.count > 0 ? `You've done ${a.progress.count}.` : null;
  return [a.hint?.todo, done, a.amount ? `+${formatSparks(a.amount)} Sparks` : null].filter(Boolean).join(' ');
}

/**
 * A More achievements chip. With a mouse its hint shows as a tooltip on hover; a tap (or click) opens it in the bubble
 * under the chips instead, which fits a phone's width, and a second tap or a tap elsewhere closes it.
 */
function AchievementChip({ a, open, onToggle }) {
  const hint = achievementHint(a);
  return (
    <span className="group relative" data-achievement-chip>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="achievement-hint"
        className={`rounded-full border px-3 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5C3BD1] ${a.earnedAt ? 'bg-[#FFF8DC] border-[#FFD966] text-[#2F1E6B]' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
      >
        {a.label} {a.earnedAt ? '✓' : '🔒'}
        {!a.earnedAt && a.progress && ` (${a.progress.count}/${a.progress.target})`}
      </button>
      {hint && !open && (
        <span role="tooltip" className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-56 rounded-lg bg-[#2F1E6B] px-3 py-2 text-xs text-white shadow-lg group-hover:block">
          {hint}
        </span>
      )}
    </span>
  );
}

function BadgeTile({ src, earned, label, sub, onOpen }) {
  return (
    <button type="button" onClick={onOpen} className="flex w-24 flex-col items-center text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5C3BD1] rounded-xl p-1">
      <span className="badge-tile">
        <img src={src} alt="" width={76} height={80} className={earned ? undefined : 'badge-locked'} />
        {!earned && <span className="badge-tile__lock" aria-hidden>🔒</span>}
      </span>
      <span className="mt-1 text-xs font-semibold text-[#2F1E6B] leading-tight">{label}</span>
      {sub && <span className="text-[11px] text-gray-500 leading-tight">{sub}</span>}
    </button>
  );
}

/**
 * Every badge the child can collect, locked until earned (levels plan §8): level and streak status,
 * the 16 streak badges, the 50 levels grouped by bracket, and the first-time and count achievements.
 * One request: GET /sparks/:childId/state, which the context reloads on open.
 */
export default function Achievements() {
  const s = useSparks();
  const { refreshState } = s;
  const [open, setOpen] = useState(null);
  const [expanded, setExpanded] = useState(null);
  // The More achievements chip whose hint is open by tap.
  const [hintKey, setHintKey] = useState(null);

  useEffect(() => {
    if (!hintKey) return undefined;
    const close = (e) => {
      if (!e.target.closest?.('[data-achievement-chip]')) setHintKey(null);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [hintKey]);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  const bracket = bracketFor(s.level, s.levelBrackets);
  const progress = levelProgress(s.lifetime, s.level, s.levelThresholds);
  const reachedAt = useMemo(() => new Map(s.levelHistory.map((h) => [h.level, h.reachedAt])), [s.levelHistory]);
  const earnedStreak = s.streakBadges.filter((b) => b.earnedAt).length;
  const isNew = s.level <= 1 && earnedStreak === 0 && !s.otherAchievements.some((a) => a.earnedAt);
  const openBracket = expanded ?? bracket?.name;
  const hinted = s.otherAchievements.find((a) => a.key === hintKey) || null;

  if (!s.levelThresholds.length) return <div className="p-6 text-gray-600">Loading your achievements…</div>;
  if (!s.enabled) return <div className="p-6 text-gray-600">Achievements are taking a short break. Check back soon!</div>;

  const levelDate = (level) => {
    if (level > s.level) return null;
    if (level === 1) return 'Start';
    const at = reachedAt.get(level);
    return at ? fmtDate(at) : 'Before levels';
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
      <section className="rounded-2xl bg-white border border-[#E6DDFB] p-4 flex flex-wrap items-center gap-4">
        <img src={levelBadgeUrl(s.level)} alt={`Level ${s.level}`} width={92} height={96} />
        <div className="min-w-[220px] flex-1">
          <h1 className="text-2xl font-bold text-[#2F1E6B]">Level {s.level}{bracket ? ` · ${bracket.name}` : ''}</h1>
          {progress ? (
            <>
              <div className="sparks-progress mt-2" aria-hidden><span style={{ width: `${progress.pct}%` }} /></div>
              <div className="mt-1 text-sm text-gray-600">{formatSparks(progress.into)} / {formatSparks(progress.span)} Sparks earned to Level {s.level + 1}</div>
            </>
          ) : (
            <div className="mt-1 text-sm text-gray-600">Level {s.level}, {bracket?.name}: you&apos;ve mastered every level!</div>
          )}
          <div className="mt-2 text-sm text-[#2F1E6B]">
            🔥 Streak: {s.dailyStreak} {s.dailyStreak === 1 ? 'day' : 'days'} · ❄ {s.streakFreezes} {s.streakFreezes === 1 ? 'freeze' : 'freezes'}
            {s.nextMilestone && <> · Next badge: {s.nextMilestone.day} days</>}
          </div>
        </div>
        <Link to="/shop" className="text-sm font-semibold text-[#5C3BD1] hover:underline">Addy&apos;s Shop →</Link>
      </section>

      {isNew && (
        <section className="rounded-2xl bg-[#FFF8DC] border border-[#FFD966] p-4 flex flex-wrap items-center gap-4">
          <div className="flex gap-2">
            <img src={streakBadgeUrl(3)} alt="" width={60} height={63} />
            <img src={levelBadgeUrl(2)} alt="" width={60} height={63} />
          </div>
          <div className="text-[#2F1E6B]">
            <div className="font-semibold">Do your first quiz to start collecting badges!</div>
            <div className="text-sm">First targets: a 3-day streak and Level 2.</div>
          </div>
        </section>
      )}

      <section>
        <h2 className="text-lg font-bold text-[#2F1E6B] mb-2">Streak badges ({earnedStreak} / {s.streakBadges.length})</h2>
        <div className="flex flex-wrap gap-2">
          {s.streakBadges.map((b) => (
            <BadgeTile
              key={b.key}
              src={streakBadgeUrl(b.day)}
              earned={!!b.earnedAt}
              label={b.name}
              sub={b.earnedAt ? fmtDate(b.earnedAt) : `Reach a ${b.day}-day streak`}
              onOpen={() => setOpen({ src: streakBadgeUrl(b.day), title: b.name, detail: b.earnedAt ? `${b.day}-day streak · earned ${fmtDate(b.earnedAt)}` : `Reach a ${b.day}-day streak`, earned: !!b.earnedAt })}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-bold text-[#2F1E6B] mb-2">Levels ({s.level} / {s.levelThresholds.length})</h2>
        <div className="space-y-2">
          {s.levelBrackets.map((br) => {
            const levels = [];
            for (let l = br.from; l <= br.to; l += 1) levels.push(l);
            const got = levels.filter((l) => l <= s.level).length;
            const isOpen = openBracket === br.name;
            return (
              <div key={br.name} className="rounded-xl border border-[#E6DDFB] bg-white">
                <button type="button" onClick={() => setExpanded(isOpen ? '' : br.name)} aria-expanded={isOpen} className="w-full flex items-center justify-between px-3 py-2 text-left">
                  <span className="font-semibold text-[#2F1E6B]">{br.name}</span>
                  <span className="text-sm text-gray-600">{got}/{levels.length} {got === levels.length ? '✓' : got === 0 ? '🔒' : ''}</span>
                </button>
                {isOpen && (
                  <div className="flex flex-wrap gap-2 px-2 pb-2">
                    {levels.map((l) => (
                      <BadgeTile
                        key={l}
                        src={levelBadgeUrl(l)}
                        earned={l <= s.level}
                        label={`Level ${l}`}
                        sub={levelDate(l) || `${formatSparks(s.levelThresholds[l - 1])} Sparks earned`}
                        onOpen={() => setOpen({ src: levelBadgeUrl(l), title: `Level ${l}`, detail: l <= s.level ? `Reached: ${levelDate(l)}` : `Earn ${formatSparks(s.levelThresholds[l - 1])} Sparks in total`, earned: l <= s.level })}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-bold text-[#2F1E6B] mb-2">More achievements</h2>
        <div className="flex flex-wrap gap-2">
          {s.otherAchievements.map((a) => (
            <AchievementChip key={a.key} a={a} open={hintKey === a.key} onToggle={() => setHintKey((k) => (k === a.key ? null : a.key))} />
          ))}
        </div>
        {hinted && (
          <p id="achievement-hint" role="status" className="mt-2 rounded-lg bg-[#2F1E6B] px-3 py-2 text-sm text-white">
            <span className="font-semibold">{hinted.label}:</span> {achievementHint(hinted)}
          </p>
        )}
      </section>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" onClick={() => setOpen(null)}>
          <div className="rounded-3xl bg-white p-6 text-center shadow-2xl max-w-xs mx-4" onClick={(e) => e.stopPropagation()}>
            <img src={open.src} alt="" width={160} height={168} className={`mx-auto ${open.earned ? '' : 'badge-locked'}`} />
            <div className="mt-2 text-xl font-bold text-[#2F1E6B]">{open.title}</div>
            <div className="text-sm text-gray-600">{open.detail}</div>
            <button type="button" onClick={() => setOpen(null)} className="mt-4 px-6 py-2 rounded-full bg-[#5C3BD1] text-white font-semibold">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
